import type { AICoachQuickReply, AICoachQuickReplyIntent } from '@/api/types';

const MAX_QUICK_REPLIES = 4;
const MAX_QUICK_REPLY_LABEL_LENGTH = 48;
const MAX_QUICK_REPLY_PAYLOAD_LENGTH = 120;
const UNSAFE_QUICK_REPLY = /(?:https?:\/\/|www\.|```|<\/?(?:script|style|iframe)\b|ignore\s+(?:all\s+)?previous|system\s*prompt|developer\s+message|jailbreak|забудь\s+(?:все\s+)?(?:предыдущ|инструк)|системн(?:ый|ого)\s+промпт|игнорируй\s+(?:все\s+)?(?:предыдущ|инструк))/i;
const QUICK_REPLY_ID = /^qr_[0-9a-f]{32}$/;
const MESSAGE_ID = /^[1-9][0-9]*$/;
const INTENTS = new Set<AICoachQuickReplyIntent>([
  'continue', 'next_step', 'explain', 'show_details', 'alternative', 'confirm', 'decline',
]);

function hasUnsafeControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return (code < 32 && code !== 9 && code !== 10 && code !== 13) || code === 127;
  });
}

function cleanText(value: unknown, maxLength: number): string | null {
  if (typeof value !== 'string') return null;
  if (UNSAFE_QUICK_REPLY.test(value) || hasUnsafeControlCharacter(value)) return null;
  const text = value.replace(/\s+/g, ' ').trim();
  return text && text.length <= maxLength ? text : null;
}

export function sanitizeCoachQuickReplies(value: unknown): AICoachQuickReply[] {
  if (!Array.isArray(value)) return [];

  const replies: AICoachQuickReply[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const candidate = item as Partial<AICoachQuickReply>;
    const label = cleanText(candidate.label, MAX_QUICK_REPLY_LABEL_LENGTH);
    const payload = cleanText(candidate.payload, MAX_QUICK_REPLY_PAYLOAD_LENGTH);
    if (
      !label
      || !payload
      || typeof candidate.id !== 'string'
      || !QUICK_REPLY_ID.test(candidate.id)
      || typeof candidate.message_id !== 'string'
      || !MESSAGE_ID.test(candidate.message_id)
      || !candidate.intent
      || !INTENTS.has(candidate.intent)
    ) continue;
    const key = candidate.id;
    if (seen.has(key)) continue;
    seen.add(key);
    replies.push({
      id: candidate.id,
      message_id: candidate.message_id,
      intent: candidate.intent,
      label,
      payload,
    });
    if (replies.length === MAX_QUICK_REPLIES) break;
  }
  return replies;
}
